import dataService from '../services/data-service.js';
import dataHelper from '../services/data-helper.js';
import { types } from './mutations';

// given an array of responses [{ ok, error, data }, { ok, error, data }, ...] gets the first error string
export const getFirstError = (responses) => {
  const error = responses.find(response => response.error !== null);
  return (error) ? error.error : null;
};

export default {
  // change mode when using menu
  changeMode ({ commit }, payload) {
    commit(types.CHANGE_MODE, { ...payload });
  },
  updateTerm ({ commit, dispatch }, payload) {
    if (payload.term) {
      const term = dataHelper.sanitizeString(payload.term);
      commit(types.SEARCH_START, { term });
      dispatch('searchBics', { term }); 
    }
  },
  updateIndustry ({ commit, dispatch }, payload) {
    if (payload.industry) {
      commit(types.SECTORS_FETCH, { ...payload });
      dispatch('getSectors', payload);
    } else {
      commit(types.SECTORS_RESET);
      commit(types.CATEGORIES_RESET);
    }
  },
  updateSector ({ commit, dispatch }, payload) {
    commit(types.CATEGORIES_FETCH, { ...payload });
    if (payload.sector) {
      dispatch('getCategories', payload);
    } else {
      commit(types.CATEGORIES_RESET);
    }
  },
  updateCategory ({ commit }, payload) {
    commit(types.CATEGORY_UPDATE, { ...payload });
  },
  async searchBics ({ commit }, payload) {
    const { ok, data, error } = await dataService.getCodesByTerm(payload.term);
    // the API response is not consistent with other responses
    // it includes a key "bics": {"bics":[{"code":"G425110","desc":"....
    // other endpoints return an array, eg industries: [{"name":"Accommodation and Food Services","id":"555bb0e425a9732ca20a3116"}, ...
    if (ok) {
      commit(types.BICS_RECEIVED, { ...data });
    } else {
      commit(types.API_ERROR, { error  });
    }
  },
  async getIndustries ({ commit }, payload) {
    // payload is null, we just leave it for consistency and testing
    const { ok, data, error } = await dataService.getIndustries(payload);
    if (ok) {
      commit(types.INDUSTRIES_RECEIVED, data);
    } else {
      commit(types.API_ERROR, { error  });
    }
  },
  async getSectors ({ commit }, payload) {
    const { ok, data, error } = await dataService.getSectors(payload.industry);
    if (ok) {
      commit(types.SECTORS_RECEIVED, data);
    } else {
      commit(types.API_ERROR, { error  });
    }
  },
  async getCategories ({ commit }, payload) {
    const { ok, data, error } = await dataService.getCategories(payload.sector);
    if (ok) {
      commit(types.CATEGORIES_RECEIVED, data);
    } else {
      commit(types.API_ERROR, { error });
    }
  },
  // getIndustry is used when navigating to /browse/industry/:industry - updateIndustry is used when using the filter
  getIndustry ({ commit, dispatch }, payload) {
    commit(types.INDUSTRIES_SECTORS_FETCH, { ...payload });
    dispatch('getIndustriesAndSectors', payload);
  },
  async getIndustriesAndSectors ({ commit }, payload) {
    const promises = [dataService.getIndustries(), dataService.getSectors(payload.industry)];
    Promise.all(promises).then(responses => { 
      if (responses[0].ok && responses[1].ok) {
        commit(types.INDUSTRIES_SECTORS_RECEIVED, { industryOptions: responses[0].data, sectorOptions: responses[1].data });
      } else {
        commit(types.API_ERROR, { error: getFirstError(responses) });
      }
    });
  },
  // getSector is used when navigating to /browse/division/:division - updateSector is used when using the filter
  getSector ({ commit, dispatch }, payload) {
    commit(types.SECTOR_FETCH, { ...payload });
    dispatch('getSectorDetails', payload);
  },
  async getSectorDetails ({ commit, dispatch }, payload) {
    // retrieve the industryID to populate the sector dropdown
    const { ok, data, error } = await dataService.get('SectorDetails', payload);
    if (ok) {
      const newPayload = { industry: data.industryId, sector: data.id };
      commit(types.SECTOR_RECEIVED, newPayload);
      dispatch('getIndustriesAndSectorsAndCategories', newPayload);
    } else {
      commit(types.API_ERROR, { error });
    }
  },
  async getIndustriesAndSectorsAndCategories ({ commit }, payload) {
    const promises = [dataService.getIndustries(), dataService.getSectors(payload.industry), dataService.getCategories(payload.sector)];
    Promise.all(promises).then(responses => { 
      if (responses[0].ok && responses[1].ok, responses[2].ok) {
        commit(types.INDUSTRIES_SECTORS_CATEGORIES_RECEIVED, { industryOptions: responses[0].data, sectorOptions: responses[1].data, categoryOptions: responses[2].data });
      } else {
        commit(types.API_ERROR, { error: getFirstError(responses) });
      }
    });
  },
  // DEPRECATED - initially "browse" would only fetch the bic list and assume that all the other data was already available
  // - it now works in a more stateless way and retrieves all the data from the category ID
  // browseBics ({ commit, dispatch }, payload) {
  //   commit(types.BROWSE_START, { ...payload });
  //   dispatch('getBicsByCategory', payload);
  // },
  // async getBicsByCategory ({ commit }, payload) {
  //   const { ok, data, error } = await dataService.getCodesByClassId(payload.category);
  //   if (ok) {
  //     commit(types.BICS_BROWSE_RECEIVED, data);
  //   } else {
  //     commit(types.API_ERROR, { error });
  //   }
  // },
  browseBics ({ commit, dispatch }, payload) {
    commit(types.BROWSE_START, { ...payload });
    dispatch('getCategoryDetails', payload);
  },
  async getCategoryDetails ({ commit, dispatch }, payload) {
    // retrieve the industryID / sectorID to populate the industry / sector dropdowns
    const { ok, data, error } = await dataService.get('CategoryDetails', payload);
    if (ok) {
      const newPayload = { industry: data.industry.id, sector: data.division.id, category: data.class.id };
      commit(types.BROWSE_PARAMS_RECEIVED, newPayload);
      dispatch('getBicsByCategory', newPayload);
    } else {
      commit(types.API_ERROR, { error });
    }
  },
  async getBicsByCategory ({ commit }, payload) {
    const promises = [
      dataService.getIndustries(), 
      dataService.getSectors(payload.industry), 
      dataService.getCategories(payload.sector),
      dataService.getCodesByClassId(payload.category)
    ];
    Promise.all(promises).then(responses => { 
      if (responses[0].ok && responses[1].ok, responses[2].ok, responses[3].ok) {
        commit(types.BICS_BROWSE_RECEIVED, { 
          industryOptions: responses[0].data, 
          sectorOptions: responses[1].data, 
          categoryOptions: responses[2].data,
          bics: responses[3].data,
        });
      } else {
        commit(types.API_ERROR, { error: getFirstError(responses) });
      }
    });
  },
  selectBics ({ commit, dispatch }, payload) {
    commit(types.SELECT_START, { ...payload });
    dispatch('getBicsByCodes', payload);
  },
  async getBicsByCodes ({ commit }, payload) {
    // const { ok, data, error } = await dataService.getCodesByCodes(payload.bicsSelected);
    const { ok, data, error } = await dataService.get('SelectedCodes', payload);
    if (ok) {
      commit(types.BICS_SELECTED_RECEIVED, data);
    } else {
      commit(types.API_ERROR, { error });
    }
  },
  initSelectedBics ({ commit }, payload) {
    if ('bicsSelected' in payload) {
      commit(types.BICS_SELECTED_INIT, { ...payload }); 
    }
  },
  getAlternateBic ({ commit, dispatch }, payload) {
    commit(types.BIC_ALTERNATE_START, { ...payload });
    dispatch('getBicById', { id: payload.id });
  },
  async getBicById ({ commit }, payload) {
    const { ok, data, error } = await dataService.getCodeById(payload.id);
    if (ok) {
      commit(types.BIC_ALTERNATE_RECEIVED, data);
    } else {
      commit(types.API_ERROR, { error });
    }
  },
  clearAlternateBic ({ commit }) {
    commit(types.BIC_ALTERNATE_CLEAR);
  },
  addToWatchlist ({ commit }, payload) {
    if (payload.id) {
      commit(types.ADD_TO_WATCHLIST, payload);
    }
  },
  removeFromWatchlist ({ commit }, payload) {
    if (payload.id) {
      commit(types.REMOVE_FROM_WATCHLIST, payload);
    }
  },
  clearWatchlist ({ commit }) {
    commit(types.CLEAR_WATCHLIST);
  },
  toggleBicsHelp ({ commit }, payload) {
    if (payload.code) {
      commit(types.TOGGLE_BICS_HELP, payload);
    }
  },
  setShowSelected ({ commit }, payload) {
    commit(types.SET_SHOW_SELECTED, payload);
  },
  setDisplayList ({ commit }, payload) {
    commit(types.SET_DISPLAY_LIST, payload);
  },
}